/**
 * Lets any participant re-hear the current coaching step. Replay re-triggers the
 * assistant to speak the current part again for everyone in the call; it's only
 * shown while the assistant is idle (before Next/Continue) and audio is cached.
 */

import React from 'react'
import { Button, CircularProgress, Tooltip } from '@mui/material'
import { ArrowPathIcon } from '@heroicons/react/24/solid'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { INTERAX_COLORS } from '../../../../../constants/colors'
import { AI_COACH_STYLES } from '../constants'

interface AICoachReplayButtonProps {
  canReplay: boolean
  isReplaying: boolean
  isAISpeaking: boolean
  onReplay: () => void
}

export const AICoachReplayButton: React.FC<AICoachReplayButtonProps> = ({
  canReplay,
  isReplaying,
  isAISpeaking,
  onReplay
}) => {
  const { t } = useTranslation()

  if (!canReplay && !isReplaying) {
    return null
  }

  const isDisabled = isReplaying || isAISpeaking || !canReplay

  return (
    <Tooltip title={t('aiCoach.tooltips.replay')}>
      <span>
        <Button
          variant="outlined"
          startIcon={
            isReplaying ? (
              <CircularProgress size={16} sx={{ color: INTERAX_COLORS.sun }} />
            ) : (
              <ArrowPathIcon style={{ width: 16, height: 16 }} />
            )
          }
          onClick={onReplay}
          disabled={isDisabled}
          aria-label={t('aiCoach.buttons.replay')}
          sx={{
            color: INTERAX_COLORS.sun,
            borderColor: INTERAX_COLORS.sun40,
            fontWeight: 600,
            // Vertical padding is 1px less than the Continue button to offset the
            // 1px outline border, so both buttons render at the same height.
            fontSize: { xs: 12, sm: 13, md: 14, lg: 14 },
            padding: { xs: '9px 12px', sm: '10px 14px', md: '11px 16px', lg: '11px 16px' },
            borderRadius: AI_COACH_STYLES.borderRadius.medium,
            textTransform: 'none',
            whiteSpace: 'nowrap',
            minWidth: 'auto',
            '&:hover': {
              borderColor: INTERAX_COLORS.sun,
              backgroundColor: INTERAX_COLORS.sun20
            },
            '&:disabled': {
              color: INTERAX_COLORS.white40,
              borderColor: INTERAX_COLORS.white10
            }
          }}
        >
          {isReplaying ? t('aiCoach.buttons.replaying') : t('aiCoach.buttons.replay')}
        </Button>
      </span>
    </Tooltip>
  )
}
